import { useState } from 'react';
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

interface Hole {
  hole_number: number;
  par: number;
  handicap_index: number | null;
}

interface TeeBox {
  id: string;
  name: string;
  color: string;
  course_rating: number | null;
  slope_rating: number | null;
  player_categories: string[] | null;
}

interface Props {
  tournamentId: string;
  tournamentName: string;
}

const TEE_COLORS: Record<string, string> = {
  BLACK: '#1f2937',
  GOLD: '#d97706',
  BLUE: '#2563eb',
  WHITE: '#f9fafb',
  RED: '#dc2626',
  GREEN: '#16a34a',
  YELLOW: '#eab308',
  ORANGE: '#ea580c',
};

function buildScorecardHtml(params: {
  tournamentName: string;
  courseName: string;
  courseLocation: string;
  holes: Hole[];
  teeBoxes: TeeBox[];
  orgName: string;
  logoUrl: string | null;
  startDate: string;
  endDate: string;
}): string {
  const { tournamentName, courseName, courseLocation, holes, teeBoxes, orgName, logoUrl, startDate, endDate } = params;

  const front9 = holes.filter((h) => h.hole_number <= 9).sort((a, b) => a.hole_number - b.hole_number);
  const back9 = holes.filter((h) => h.hole_number >= 10 && h.hole_number <= 18).sort((a, b) => a.hole_number - b.hole_number);

  const frontPar = front9.reduce((s, h) => s + (h.par || 0), 0);
  const backPar = back9.reduce((s, h) => s + (h.par || 0), 0);
  const totalPar = frontPar + backPar;

  const logoHtml = logoUrl
    ? `<img src="${logoUrl}" alt="${orgName}" class="logo" />`
    : `<div class="logo-placeholder">${orgName.charAt(0)}</div>`;

  const dateRange = startDate
    ? `${new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}${endDate && endDate !== startDate ? ' – ' + new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}`
    : '';

  const teeBoxRows = teeBoxes
    .map((t) => {
      const colorDot = `<span class="tee-dot" style="background:${TEE_COLORS[t.color?.toUpperCase()] || '#6b7280'}"></span>`;
      const categories = t.player_categories?.length ? t.player_categories.join(', ') : '—';
      return `<tr>
        <td>${colorDot} ${t.name}</td>
        <td>${t.course_rating ?? '—'}</td>
        <td>${t.slope_rating ?? '—'}</td>
        <td>${categories}</td>
      </tr>`;
    })
    .join('');

  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>${tournamentName} – Scorecard</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: 'Plus Jakarta Sans', Arial, sans-serif;
      background: #fff;
      color: #1A1A1A;
      font-size: 10.5px;
      padding: 18px 22px 30px;
    }

    /* HEADER */
    .header {
      display: flex;
      align-items: center;
      gap: 16px;
      border-bottom: 3px solid #1A472A;
      padding-bottom: 14px;
      margin-bottom: 16px;
    }
    .logo { height: 56px; width: auto; object-fit: contain; flex-shrink: 0; }
    .logo-placeholder {
      height: 56px; width: 56px;
      background: #1A472A; color: #fff;
      font-size: 26px; font-weight: 700;
      display: flex; align-items: center; justify-content: center;
      border-radius: 8px; flex-shrink: 0;
    }
    .header-text { flex: 1; }
    .org-name {
      font-size: 10px; font-weight: 600; color: #1A472A;
      text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 2px;
    }
    .tournament-name {
      font-size: 20px; font-weight: 700; color: #1A1A1A; line-height: 1.2; margin-bottom: 3px;
    }
    .course-info { font-size: 11px; color: #6B7280; }
    .header-meta {
      text-align: right; font-size: 10px; color: #9CA3AF; line-height: 1.8; flex-shrink: 0;
    }
    .header-meta strong { color: #4B5563; }

    /* PLAYER INFO */
    .player-info-section {
      display: grid; grid-template-columns: repeat(4, 1fr);
      gap: 8px; margin-bottom: 14px;
    }
    .info-field {
      border-bottom: 1.5px solid #1A472A;
      padding-bottom: 3px;
    }
    .info-label {
      font-size: 8.5px; font-weight: 600; color: #9CA3AF;
      text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 8px;
    }

    /* SCORECARD TABLE */
    .scorecard-wrap { margin-bottom: 14px; }
    .section-title {
      font-size: 9px; font-weight: 700; color: #1A472A;
      text-transform: uppercase; letter-spacing: 0.08em;
      margin-bottom: 4px;
    }

    table { width: 100%; border-collapse: collapse; }

    .header-row th {
      background: #1A472A; color: #fff;
      padding: 5px 3px; text-align: center;
      font-size: 10px; font-weight: 700;
      border: 1px solid #15603a;
    }
    .header-row th.label-cell { text-align: left; padding-left: 6px; min-width: 60px; }

    .par-row td { background: #F0FDF4; }
    .hcp-row td { background: #F9FAFB; }

    td, th {
      border: 1px solid #E5E7EB;
      text-align: center;
      padding: 4px 3px;
      min-width: 22px;
    }
    td.label-cell { text-align: left; padding-left: 6px; font-size: 9px; font-weight: 600; color: #4B5563; }
    td.player-label { font-size: 10px; font-weight: 600; color: #1A1A1A; }
    td.par-cell { font-weight: 700; font-size: 10px; color: #1A472A; }
    td.hcp-cell { font-size: 9px; color: #9CA3AF; }
    th.hole-num { font-size: 11px; width: 26px; }
    th.subtotal-cell, td.subtotal-cell {
      background: #F3F4F6; font-weight: 700; font-size: 10px; min-width: 30px;
    }
    td.total-cell {
      background: #ECFDF5; font-weight: 700; font-size: 10px; min-width: 34px; border: 1.5px solid #1A472A;
    }
    td.score-cell { height: 22px; min-width: 26px; }

    .score-row.player-a td { background: #fff; }
    .score-row.player-b td { background: #F8FAFC; }

    /* COMBINED TABLE: front + back in one table with OUT/IN/TOTAL */
    .full-table th.in-cell, .full-table td.in-cell {
      background: #F3F4F6; font-weight: 700;
    }

    /* TEE BOX INFO */
    .tee-section { margin-bottom: 14px; }
    .tee-table th {
      background: #F9FAFB; font-size: 9px; font-weight: 600;
      color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em;
      padding: 5px 8px; text-align: left;
    }
    .tee-table td { padding: 5px 8px; font-size: 10px; text-align: left; }
    .tee-dot {
      display: inline-block; width: 10px; height: 10px;
      border-radius: 50%; margin-right: 4px; vertical-align: middle;
      border: 1px solid rgba(0,0,0,0.15);
    }

    /* SIGNATURE & ATTESTATION */
    .attestation {
      display: grid; grid-template-columns: 1fr 1fr 1fr;
      gap: 24px; margin-top: 14px; padding-top: 12px;
      border-top: 1px solid #E5E7EB;
    }
    .sig-field { border-bottom: 1px solid #6B7280; padding-bottom: 2px; margin-top: 16px; }
    .sig-label { font-size: 8.5px; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.05em; }
    .att-title { font-size: 9px; font-weight: 700; color: #1A472A; margin-bottom: 6px; text-transform: uppercase; }

    /* FOOTER */
    .footer {
      display: flex; justify-content: space-between; align-items: center;
      margin-top: 12px; padding-top: 8px; border-top: 1px solid #E5E7EB;
      font-size: 8.5px; color: #D1D5DB;
    }

    /* PRINT */
    @media print {
      body { padding: 6px 10px 16px; font-size: 9.5px; }
      @page { margin: 10mm 12mm; size: A4 landscape; }
    }
  </style>
</head>
<body>

  <!-- HEADER -->
  <div class="header">
    ${logoHtml}
    <div class="header-text">
      <div class="org-name">${orgName}</div>
      <div class="tournament-name">${tournamentName}</div>
      <div class="course-info">${courseName}${courseLocation ? ' &bull; ' + courseLocation : ''}</div>
    </div>
    <div class="header-meta">
      <div>${dateRange ? '<strong>' + dateRange + '</strong>' : ''}</div>
      <div>Official Scorecard</div>
      <div>Printed ${today}</div>
    </div>
  </div>

  <!-- PLAYER INFO FIELDS -->
  <div class="player-info-section">
    <div class="info-field"><div class="info-label">Player Name</div></div>
    <div class="info-field"><div class="info-label">Handicap</div></div>
    <div class="info-field"><div class="info-label">Club / Category</div></div>
    <div class="info-field"><div class="info-label">Date</div></div>
  </div>

  <!-- SCORECARD TABLE -->
  <div class="scorecard-wrap">
    <table class="full-table">
      <thead>
        <tr class="header-row">
          <th class="label-cell">Hole</th>
          ${front9.map((h) => `<th class="hole-num">${h.hole_number}</th>`).join('')}
          <th class="subtotal-cell">OUT</th>
          ${back9.map((h) => `<th class="hole-num">${h.hole_number}</th>`).join('')}
          ${back9.length > 0 ? '<th class="in-cell subtotal-cell">IN</th>' : ''}
          <th class="total-cell" style="background:#1A472A;color:#fff;">TOT</th>
        </tr>
      </thead>
      <tbody>
        <!-- Par row -->
        <tr class="par-row">
          <td class="label-cell">Par</td>
          ${front9.map((h) => `<td class="par-cell">${h.par}</td>`).join('')}
          <td class="subtotal-cell par-cell">${frontPar || ''}</td>
          ${back9.map((h) => `<td class="par-cell">${h.par}</td>`).join('')}
          ${back9.length > 0 ? `<td class="in-cell subtotal-cell par-cell">${backPar || ''}</td>` : ''}
          <td class="total-cell par-cell">${totalPar || ''}</td>
        </tr>
        <!-- HCP row -->
        <tr class="hcp-row">
          <td class="label-cell">HCP</td>
          ${front9.map((h) => `<td class="hcp-cell">${h.handicap_index ?? ''}</td>`).join('')}
          <td class="subtotal-cell hcp-cell"></td>
          ${back9.map((h) => `<td class="hcp-cell">${h.handicap_index ?? ''}</td>`).join('')}
          ${back9.length > 0 ? '<td class="in-cell subtotal-cell hcp-cell"></td>' : ''}
          <td class="total-cell hcp-cell"></td>
        </tr>
        <!-- Score rows -->
        <tr class="score-row player-a">
          <td class="label-cell player-label">Score</td>
          ${front9.map(() => '<td class="score-cell"></td>').join('')}
          <td class="subtotal-cell score-cell"></td>
          ${back9.map(() => '<td class="score-cell"></td>').join('')}
          ${back9.length > 0 ? '<td class="in-cell subtotal-cell score-cell"></td>' : ''}
          <td class="total-cell score-cell"></td>
        </tr>
        <tr class="score-row player-b">
          <td class="label-cell" style="font-size:9px;color:#9CA3AF;">Stableford</td>
          ${front9.map(() => '<td class="score-cell"></td>').join('')}
          <td class="subtotal-cell score-cell"></td>
          ${back9.map(() => '<td class="score-cell"></td>').join('')}
          ${back9.length > 0 ? '<td class="in-cell subtotal-cell score-cell"></td>' : ''}
          <td class="total-cell score-cell"></td>
        </tr>
        <tr class="score-row player-a">
          <td class="label-cell" style="font-size:9px;color:#9CA3AF;">Net Score</td>
          ${front9.map(() => '<td class="score-cell"></td>').join('')}
          <td class="subtotal-cell score-cell"></td>
          ${back9.map(() => '<td class="score-cell"></td>').join('')}
          ${back9.length > 0 ? '<td class="in-cell subtotal-cell score-cell"></td>' : ''}
          <td class="total-cell score-cell"></td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- TEE BOX INFO -->
  ${teeBoxRows ? `
  <div class="tee-section">
    <div class="section-title">Tee Information</div>
    <table class="tee-table">
      <thead>
        <tr>
          <th>Tee</th>
          <th>Course Rating</th>
          <th>Slope Rating</th>
          <th>Player Categories</th>
        </tr>
      </thead>
      <tbody>${teeBoxRows}</tbody>
    </table>
  </div>` : ''}

  <!-- ATTESTATION -->
  <div class="attestation">
    <div>
      <div class="att-title">Player Signature</div>
      <div class="sig-field"></div>
      <div class="sig-label">Signature &amp; Date</div>
    </div>
    <div>
      <div class="att-title">Marker Signature</div>
      <div class="sig-field"></div>
      <div class="sig-label">Marker Name &amp; Signature</div>
    </div>
    <div>
      <div class="att-title">Official Verification</div>
      <div class="sig-field"></div>
      <div class="sig-label">Tournament Official</div>
    </div>
  </div>

  <!-- FOOTER -->
  <div class="footer">
    <div>${orgName} &bull; ${tournamentName}</div>
    <div>I certify that this scorecard is correct and complete</div>
    <div>Printed ${today}</div>
  </div>

  <script>window.onload = function() { window.print(); };</script>
</body>
</html>`;
}

export default function ScorecardPDFButton({ tournamentId, tournamentName }: Props) {
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      let logoUrl: string | null = null;
      let orgName = 'Golf Tournament';
      let courseName = '';
      let courseLocation = '';
      let holes: Hole[] = [];
      let teeBoxes: TeeBox[] = [];
      let startDate = '';
      let endDate = '';

      const [
        { data: logoData },
        { data: titleData },
        { data: tournamentData },
        { data: tournamentCourse },
      ] = await Promise.all([
        supabase.from('logos').select('url').eq('type', 'primary').eq('location', 'header').eq('is_active', true).maybeSingle(),
        supabase.from('system_settings').select('value').eq('key', 'site_title').maybeSingle(),
        supabase.from('tournaments').select('start_date, end_date').eq('id', tournamentId).maybeSingle(),
        supabase.from('tournament_courses').select('course_id').eq('tournament_id', tournamentId).maybeSingle(),
      ]);

      if (logoData?.url) logoUrl = logoData.url;
      if (titleData?.value) orgName = String(titleData.value);

      if (tournamentData) {
        startDate = tournamentData.start_date ?? '';
        endDate = tournamentData.end_date ?? '';
      }

      if (tournamentCourse?.course_id) {
        const courseId = tournamentCourse.course_id;

        const [{ data: courseData }, { data: holesData }, { data: teeData }] = await Promise.all([
          supabase.from('courses').select('name, city, country').eq('id', courseId).maybeSingle(),
          supabase.from('holes').select('hole_number, par, handicap_index').eq('course_id', courseId).order('hole_number'),
          supabase.from('tee_boxes').select('id, name, color, course_rating, slope_rating, player_categories').eq('course_id', courseId).order('sort_order'),
        ]);

        if (courseData) {
          courseName = courseData.name ?? '';
          courseLocation = [courseData.city, courseData.country].filter(Boolean).join(', ');
        }
        if (holesData) holes = holesData as Hole[];
        if (teeData) teeBoxes = teeData as TeeBox[];
      }

      if (holes.length === 0) {
        for (let i = 1; i <= 18; i++) {
          holes.push({ hole_number: i, par: 4, handicap_index: i });
        }
      }

      const html = buildScorecardHtml({
        tournamentName,
        courseName,
        courseLocation,
        holes,
        teeBoxes,
        orgName,
        logoUrl,
        startDate,
        endDate,
      });

      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate scorecard');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleGenerate}
      disabled={loading}
      className="px-4 py-2 bg-golf-green text-white font-medium rounded-lg hover:bg-golf-green/90 transition-colors inline-flex items-center space-x-2 disabled:opacity-60"
      title="Print blank scorecard for players"
    >
      <DocumentArrowDownIcon className="w-5 h-5" />
      <span>{loading ? 'Generating...' : 'Scorecard PDF'}</span>
    </button>
  );
}
